import { Link, usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { ExternalLink } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { Campaign, Charity, Project } from '../../Shared/types';
import { Icon } from '../../Shared/UI/Icon';
import { useRouter } from '../router';
import { PageProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import { tabClasses } from '../UI/helpers';
import { CampaignLogo } from './CampaignLogo';
import CampaignToggle from './CampaignToggle';

export function CampaignLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { props: { charity, campaign }, component } = usePage<PageProps<{
    campaign: Campaign & {
      project: Project;
      toggleable: boolean;
    };
  }>>();

  return (
    <>
      <CampaignHeader campaign={campaign} charity={charity} component={component} />
      {children}
    </>
  );
}

export function CampaignHeader({
  campaign,
  charity,
  component,
}: {
  campaign: Campaign & {
    project: Project;
    toggleable: boolean;
  };
  charity: Charity;
  component?: string;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  return (
    <Header>
      <Container className="!pb-0">
        <div className="flex items-center justify-between w-full">
          <HeaderTitle className={classNames(!campaign?.enabled && 'opacity-50')}>
            <span className="mr-3">
              <CampaignLogo campaign={campaign} charity={charity} size="lg" />
            </span>
            {campaign.title}
          </HeaderTitle>

          <div className="flex items-center space-x-6">
            <a
              href={campaign.url}
              target="_blank"
              rel="noopener noreferrer"
              data-supporta-embed="false"
              className="font-medium"
            >
              {t('backend:visit')}
              <Icon className="ml-1">
                <ExternalLink />
              </Icon>
            </a>
            <CampaignToggle campaign={campaign} />
          </div>
        </div>

        <div className="mt-2 space-x-4">
          <Link
            href={routes.campaign_page(campaign.id)}
            className={tabClasses({ active: component === 'Backend/Campaigns/CampaignPage' })}
          >
            {t('backend:about')}
          </Link>
          <Link
            href={routes.campaign_sent_mails_page(campaign.id)}
            className={tabClasses({ active: component === 'Backend/Campaigns/SentMailsPage' })}
          >
            {t('backend:sent_emails')}
          </Link>
        </div>
      </Container>
    </Header>
  );
}
